import {
  Box,
  Button,
  chakra,
  Flex,
  Image,
  shouldForwardProp,
  Text,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { parseSomething } from "../../utils/parseSomething";
import { useDispatch, useSelector } from "react-redux";
import { isValidMotionProp, motion, AnimatePresence } from "framer-motion";
import { COMMON_WEB_PATH } from "../../constant";
import Count from "./components/Count";
import dayjs from "dayjs";
import useSound from "../../hook/useSound";
import {
  CLEAR_SOCKET_MESSAGE,
  notice,
  SAVE_SOCKET_MESSAGE,
} from "../../redux/action/action";
import { useTranslation } from "react-i18next";
import {
  storeCurrencyExchange,
  triggerTransferGivenUpPoint,
  SHOW_MAINTAIN_ALERT,
  TRANSFER_READY,
} from "../../redux/action/member/memberAction";
import earningMusic from "../../images/music/earning-money.mp3";
import useCheckLogin from "../../hook/useCheckLogin";
import { useNavigate, useSearchParams } from "react-router-dom";
import CommonAlertModal from "../../components/Common/commonAlertModal";
import EndGameDetail from "../../components/EndGameDetail/EndGameDetail";
import { useTransferInGameHandler } from "../Game/components/TransferInGame/transferInGameHandler";
import { formatNumber } from "../../utils/formatNumber";
import MainBalancePending from "./components/MainBalancePending";

const gradientStyle = {
  background:
    "linear-gradient(180deg, rgba(2,0,36,1) 0%, rgba(247,249,79,1) 0%, rgba(254,238,54,1) 50%, rgba(245,174,12,1) 75%, rgba(236,208,59,1) 100%)",
  WebkitBackgroundClip: "text",
  backgroundClip: "text",
  color: "transparent",
};

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const animationVariants = {
  hidden: {
    scale: 0,
    opacity: 0,
    y: -20,
    rotate: -10,
  },
  visible: {
    scale: 1.5,
    opacity: 1,
    y: 0,
    rotate: 0,
    transition: {
      type: "spring",
      stiffness: 120,
      damping: 15,
      mass: 0.8,
      duration: 0.8,
      ease: [0.25, 0.46, 0.45, 0.94], // cubic-bezier for smooth easing
      when: "beforeChildren",
      staggerChildren: 0.15,
    },
  },
  exit: {
    scale: 0.8,
    opacity: 0,
    y: 20,
    rotate: 5,
    transition: {
      duration: 0.3,
      ease: [0.55, 0.085, 0.68, 0.53], // smooth exit easing
    },
  },
};

const HEARTBEAT_INTERVAL = 30000;

const EarnMoneyFromSocket = () => {
  const { t } = useTranslation();
  const i18n_common = (key, props) => t(`common.${key}`, { ...props });
  const i18n_backend = (key) => t(`backend_response.${key}`);

  const toast = useToast();

  const [isVisible, setIsVisible] = useState(false);
  const [socketUrl, setSocketUrl] = useState(process.env.REACT_APP_WS_HOST);
  const [messages, setMessages] = useState([]);
  const [giveUpState, setGiveUpState] = useState({
    show: false,
    previousBalance: 0,
    withdrawalNumber: "",
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const memberInfo = useSelector((state) => state.isMemberInfo);
  const isSocketMessage = useSelector((state) => state.isSocketMessage);
  const isTransferInGameState = useSelector(
    (state) => state.isTransferInGameState,
  );
  const gameConfig = useSelector((state) => state.isGameConfig);

  const { message, show, rawMessage } = isSocketMessage || {};

  const { handleTransferAllIn } = useTransferInGameHandler();

  const { uid, cagent_uid } = memberInfo || {};

  const currentGameUid = gameConfig?.gameUid ?? null;
  const { isLogin } = useCheckLogin();

  const earningSound = useSound({ customSound: "earning_money" });

  const didUnmount = useRef(false);

  const {
    sendMessage,
    sendJsonMessage,
    lastMessage,
    lastJsonMessage,
    readyState,
    getWebSocket,
  } = useWebSocket(socketUrl, {
    queryParams: {
      key: "wsky",
    },
    onOpen: () => console.log("連線成功"),
    shouldReconnect: (closeEvent) => {
      /*
      useWebSocket will handle unmounting for you, but this is an example of a 
      case in which you would not want it to automatically reconnect
    */
      return didUnmount.current === false;
    },
    reconnectAttempts: 9999,
    reconnectInterval: 1000,
  });

  const socketReceivedData = useMemo(() => {
    if (lastMessage?.data) {
      return parseSomething(lastMessage.data);
    }
  }, [lastMessage?.timeStamp]);

  const compareMemberData = useMemo(() => {
    if (socketReceivedData?.member_info_uid == uid) {
      return true;
    }
    return false;
  }, [socketReceivedData, uid]);

  const handleConfirm = () => {
    dispatch(CLEAR_SOCKET_MESSAGE());
  };

  const handleCloseMessage = (id) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, isVisible: false } : msg)),
    );
    setTimeout(() => {
      setMessages((prev) => prev.filter((msg) => msg.id !== id));
    }, 300);
  };

  const responseSocketModal = useMemo(() => {
    if (show) {
      return (
        <CommonAlertModal
          openModal={show}
          setOpenModal={handleConfirm}
          priorityContent={rawMessage ? message : i18n_backend(message)}
        />
      );
    }
  }, [show, message]);

  const responseComponents = useMemo(() => {
    return (
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <Flex direction={"column"} gap="10px" alignItems={"center"}>
          {messages?.map((msg) => {
            const { amount = 0, id, isVisible } = msg;
            return (
              <ChakraBox
                key={id}
                variants={animationVariants}
                initial="hidden"
                animate={isVisible ? "visible" : "exit"}
                bg={"rgba(0,0,0,0.5)"}
                px="30px"
                py="10px"
                borderRadius={"common"}
                bgRepeat="no-repeat"
                bgSize="100% 100%"
                bgPosition="center"
                position="relative"
                onAnimationComplete={(definition) => {
                  if (definition === "exit") {
                    setMessages((prev) => prev.filter((m) => m.id !== id));
                  }
                }}
              >
                <Box
                  as="button"
                  position="absolute"
                  top="6px"
                  right="10px"
                  color="white"
                  fontSize="20px"
                  lineHeight="1"
                  cursor="pointer"
                  onClick={() => handleCloseMessage(id)}
                  zIndex={1}
                >
                  ✕
                </Box>
                <Flex
                  gap="10px"
                  alignItems="center"
                  fontSize="90px"
                  fontWeight={900}
                  {...gradientStyle}
                >
                  <Text>+</Text>
                  <Text>
                    {/* <Count from={0} to={Number(amount)} /> */}
                    {Number(amount)}
                  </Text>
                </Flex>
              </ChakraBox>
            );
          })}
        </Flex>{" "}
      </Box>
    );
  }, [messages, dispatch]);

  useEffect(() => {
    return () => {
      didUnmount.current = true;
    };
  }, []);

  useEffect(() => {
    if (socketReceivedData) {
      const { type, member_info_uid, data, memo } = socketReceivedData;
      if (type === "deposit" && member_info_uid === uid && data != null) {
        const { amount, exchange } = data || {};
        if (amount == "error") {
          return dispatch(
            notice({
              title: i18n_backend("Server Error"),
              type: "error",
            }),
          );
        }
        dispatch(storeCurrencyExchange(exchange || {}));

        /**
         * @description 當收到存款(入鈔)的訊息時，會觸發 all-in-la API 將現金轉進遊戲
         */
        handleTransferAllIn();

        new Audio(earningMusic).play();

        const currentTime = dayjs().unix(); // 使用秒級時間戳進行分組
        const messageWithId = {
          amount: amount,
          id: currentTime,
          isVisible: true,
        };

        setMessages((prev) => {
          // 檢查是否有相同 ID（相同秒數）的訊息
          const existingMessageIndex = prev.findIndex(
            (msg) => msg.id === messageWithId.id,
          );

          if (existingMessageIndex !== -1) {
            // 如果找到相同 ID，合併 amount
            const updatedMessages = [...prev];
            updatedMessages[existingMessageIndex] = {
              ...updatedMessages[existingMessageIndex],
              amount: (
                Number(updatedMessages[existingMessageIndex].amount) +
                Number(messageWithId.amount)
              ).toString(),
              isVisible: true, // 確保合併後的訊息是可見的
            };
            return updatedMessages;
          } else {
            // 如果沒有相同 ID，添加新訊息
            return [...prev, messageWithId];
          }
        });

        setTimeout(() => {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === messageWithId.id ? { ...msg, isVisible: false } : msg,
            ),
          );
        }, 4000);
      } else if (
        type === "transCode" &&
        member_info_uid === uid &&
        data != null
      ) {
        if (data == "fails") {
          dispatch(
            SAVE_SOCKET_MESSAGE({
              message: memo,
              show: true,
            }),
          );
        } else {
          dispatch(CLEAR_SOCKET_MESSAGE());
          dispatch(triggerTransferGivenUpPoint(true));

          setTimeout(() => {
            //RESET
            dispatch(triggerTransferGivenUpPoint(false));
          }, 5000);
        }
      } else if (type === "error" && member_info_uid === uid && data != null) {
        dispatch(
          SAVE_SOCKET_MESSAGE({
            message: data,
            show: true,
          }),
        );
      } else if (type === "update_member") {
        if (isLogin) {
          dispatch({ type: "GET_MEMBER_DATA" });
        }
      } else if (type === "maintain_notice") {
        dispatch(
          SHOW_MAINTAIN_ALERT({
            state: true,
            maintainTime: data,
          }),
        );
      } else if (type === "maintain_notice_member") {
        if (data?.cagent_uid != null) {
          if (String(cagent_uid) === String(data.cagent_uid)) {
            dispatch(
              SAVE_SOCKET_MESSAGE({
                message: data.msg,
                show: true,
                rawMessage: true,
              })
            );
            setTimeout(() => {
              navigate("/");
            }, 5000);
          }
          return;
        } else if (Number(member_info_uid) === Number(uid)) {
          if (data?.game_id != null) {
            if (
              Array.isArray(data.game_id) &&
              data.game_id.some((id) => String(id) === String(currentGameUid))
            ) {
              dispatch(
                SAVE_SOCKET_MESSAGE({
                  message: data.msg,
                  show: true,
                  rawMessage: true,
                })
              );
              setTimeout(() => {
                navigate("/");
              }, 5000);
            }
            return;
          }

          dispatch(
            SAVE_SOCKET_MESSAGE({
              message: data,
              show: true,
            })
          );
          setTimeout(() => {
            window.location.reload();
          }, 50000);
        }
      } else if (type == "reload") {
        window.location.reload();
      } else if (type == "transfer_in_game" || type == "CHECK_RREQUEST") {
        const { request_id, amount, message, platform, status } = data || {};
        if (member_info_uid !== uid) return;

        const current_request_id = localStorage.getItem(
          "transfer_in_game_request_id",
        );

        if (status === "pending") {
          localStorage.setItem("transfer_in_game_request_id", request_id);
          return;
        } else {
          if (current_request_id == request_id) {
            if (status === "rollback") {
              handleTransferAllIn();
              return;
            }
            localStorage.removeItem("transfer_in_game_request_id");

            dispatch(TRANSFER_READY());
            toast({
              title: i18n_common("提醒您"),
              description: i18n_common(status, {
                amount: formatNumber(amount),
                platform: platform,
                status: status,
              }),
              status: "success",
              duration: 7000,
              isClosable: true,
              position: "top",
            });
          }
        }
      } else if (type === "mac_giveup") {
        if (Number(member_info_uid) === uid) {
          dispatch({ type: "GET_BALANCE" });
          setGiveUpState({
            show: true,
            previousBalance: data?.balance,
            withdrawalNumber: data?.withdrawal_number,
          });
        }
      } else if (type === "mac_giveup_error") {
        dispatch(
          SAVE_SOCKET_MESSAGE({
            message: data?.message,
            show: true,
          }),
        );
      } else if (type === "update_only_one_member") {
        if (isLogin) {
          if (Number(member_info_uid) === uid) {
            dispatch({ type: "GET_MEMBER_DATA" });
          }
        }
      }
    }
  }, [socketReceivedData, uid]);

  useEffect(() => {
    const heartbeat = setInterval(() => {
      sendMessage("ping");
    }, HEARTBEAT_INTERVAL);

    return () => {
      clearInterval(heartbeat);
    };
  }, [sendMessage]);

  useEffect(() => {
    /**
     * @description 處理遊戲中轉點的邏輯，發生於101,102
     * 用戶重整時，將 localStorage 中的轉點請求單號傳給後端，後端依單號回傳結果
     * 結果處理依照 WebSocket 回傳的 type 決定
     */
    const current_request_id = localStorage.getItem(
      "transfer_in_game_request_id",
    );

    if (current_request_id && uid && isTransferInGameState === "pending") {
      sendJsonMessage({
        type: "CHECK_RREQUEST",
        request_id: current_request_id,
        member_info_uid: uid,
      });
    }
  }, [uid, isTransferInGameState]);

  return (
    <Box zIndex={999999} position={"relative"}>
      {/* <AnimatePresence>{responseComponents}</AnimatePresence> */}
      <AnimatePresence>{responseComponents}</AnimatePresence>
      <MainBalancePending />
      {responseSocketModal}

      {giveUpState.show && (
        <EndGameDetail
          onClose={() => {
            setGiveUpState({
              show: false,
              previousBalance: 0,
              withdrawalNumber: "",
            });
          }}
          previousBalance={giveUpState.previousBalance}
          transCode={""}
          withdrawalNumber={giveUpState.withdrawalNumber}
        />
      )}
    </Box>
  );
};

export default EarnMoneyFromSocket;
